import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ValidationError, ValidationErrorComponent } from '@/components/validation-error/validation-error';
import { regexes } from '@/constants/regexes';
import { useSharedState, useUpdateSharedState } from '@/hooks/state/shared-state';
import { useTheaterContext, useUpdateTheaterContext } from '@/hooks/state/theater';
import { useTheatersContext } from '@/hooks/state/theaters';
import { useZipCodeTheaterSearch } from '@/hooks/theater-zip-code-search';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { createFormErrorHandler } from '@/utils/form-error-handler';
import { getTheaterUrlPath } from '@/utils/theaters/theater-url';
import { Theater } from '@cinemark/content-api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import userLocationStyles from './user-location.module.scss';

type ZipSearchFormProps = {
  close?: () => void;
  isClosing?: boolean;
};

const dataLinkCategory = 'Theatre Zip Search';

export const ZipSearchForm = ({ close, isClosing }: ZipSearchFormProps): React.JSX.Element => {
  const [isSearching, setIsSearching] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const locationPanelRef = useRef<HTMLDivElement>(null);
  const pendingNavigation = useRef<string | null>(null);
  const { searchByZip } = useZipCodeTheaterSearch();
  const { data: theater } = useTheaterContext();
  const { data: theaters } = useTheatersContext();
  const { mutateAsync: updateTheaterContext } = useUpdateTheaterContext();
  const { data: sharedState } = useSharedState();
  const { mutateAsync: updateSharedState } = useUpdateSharedState();
  const limitedTheaters = useMemo(() => theaters?.data.slice(0, 3), [theaters]);
  const displayZip = theater?.zipcode || sharedState?.lastTextSearch?.toString() || theaters?.data[0].zipcode;

  const formSchema = z.object({
    zipCode: z.string({
      message: 'ZIP code cannot be empty.',
    }).regex(regexes.zipCode, {
      message: 'The value for the ZIP Code field is invalid.',
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      zipCode: '',
    },
    resolver: zodResolver(formSchema),
  });

  const handleFormErrors = createFormErrorHandler<z.infer<typeof formSchema>>(setValidationErrors);

  const handleClear = (): void => {
    form.reset();
  };

  const searchZipCode = async (values: z.infer<typeof formSchema>): Promise<void> => {
    setIsSearching(true);
    setValidationErrors([]);
    const result = await searchByZip(values.zipCode, 'search-and-redirect');

    if (result.error) {
      setValidationErrors([{ messages: [result.error] }]);
      setIsSearching(false);
    } else {
      close?.();
    }
  };

  const handleMoreNearbyTheatresClick = (event: React.MouseEvent): void => {
    event.preventDefault();
    pendingNavigation.current = '/theatres?tab=nearby';
    close?.();
  };

  const handleTheaterClick = async (theater: Theater, event: React.MouseEvent): Promise<void> => {
    event.preventDefault();
    setValidationErrors([]);

    if (theater) {
      const theaterUrl = getTheaterUrlPath(theater);
      await updateSharedState({
        lastTextSearch: theater.zipcode ? parseInt(theater.zipcode) : undefined,
        lastViewedTheaterId: theater.theaterId,
        lastViewedTheaterUrl: theaterUrl,
      });
      await updateTheaterContext(theater);
      pendingNavigation.current = theaterUrl;
      close?.();
    } else {
      close?.();
    }
  };

  useEffect(() => {
    const container = locationPanelRef.current;
    if (isClosing && container) {
      const handleAnimationEnd = (): void => {
        if (pendingNavigation.current) {
          window.location.assign(pendingNavigation.current);
          pendingNavigation.current = null;
        }
      };
      container.addEventListener('animationend', handleAnimationEnd);
      return (): void => {
        container.removeEventListener('animationend', handleAnimationEnd);
      };
    }
  }, [isClosing]);

  return (
    <Card ref={locationPanelRef}
      className={clsx(
        'fixed top-0 right-0 md:right-auto left-0 md:left-auto w-full max-w-[440px] min-[1440px]:max-w-[600px]',
        isClosing ? userLocationStyles.closing : userLocationStyles['location-search-container']
      )}
    >
      <CardContent className="flex flex-col gap-sm">
        <ValidationErrorComponent errors={validationErrors} />

        <form className="md:min-w-[320px] !flex-row items-end justify-between gap-xs"
          onSubmit={form.handleSubmit(searchZipCode, handleFormErrors)}
        >
          <Controller control={form.control}
            name="zipCode"
            render={({ field, fieldState }) =>
              <Field>
                <FieldLabel required
                  dataTest="label-zip-code"
                  error={!!fieldState.error}
                  htmlFor="input-zip-code"
                >
                  Search by ZIP Code
                </FieldLabel>
                <Input {...field}
                  aria-invalid={fieldState.invalid}
                  data-test="input-zip-code"
                  id="input-zip-code"
                  maxLength={5}
                  type="number"
                />
              </Field>
            }
          />

          <Button data-test="button-zip-code-search"
            disabled={isSearching}
            loadingCenter="within"
            showLoading={isSearching}
            type="submit"
          >
            Search
          </Button>

          <button className="sr-only"
            data-test="button-zip-code-reset"
            type="reset"
            onClick={handleClear}
          >
            Reset ZIP Code
          </button>
        </form>

        {limitedTheaters && !isSearching &&
          <>
            <hr className="my-5" />
            {displayZip &&
              <h3 className="text-sm font-semibold mb-3"
                data-test="theatres-near-zip"
              >
                Theatres Near {displayZip}
              </h3>
            }
            <div className="flex flex-col gap-5">
              {limitedTheaters?.map((theater) => {
                return (
                  <CinemarkLink key={theater.id}
                    useAnchor
                    className="flex justify-between gap-5"
                    dataLinkCategory={dataLinkCategory}
                    dataLinkName={theater.title}
                    dataTest={`link-theater-${theater.theaterId}`}
                    href={getTheaterUrlPath(theater)}
                    onClick={event => handleTheaterClick(theater, event)}
                  >
                    <span className="text-sm"
                      data-test={`theater-name-${dataTestTransformer(theater.title)}`}
                    >
                      {theater.title}
                      <br />
                      <span className="text-xs text-gray-500"
                        data-test={`theater-location-${dataTestTransformer(theater.title)}`}
                      >
                        {theater.city}, {theater.state}
                      </span>
                    </span>
                    <span className="text-sm text-primary font-semibold"
                      data-test={`theater-details-${dataTestTransformer(theater.title)}`}
                    >
                      Details
                    </span>
                  </CinemarkLink>
                );
              })}
              <CinemarkLink useAnchor
                className="flex justify-between gap-5 text-xs text-primary font-semibold center"
                dataLinkCategory={dataLinkCategory}
                dataLinkName="More Nearby Theatres"
                dataTest="link-more-nearby-theatres"
                href="/theatres?tab=nearby"
                onClick={handleMoreNearbyTheatresClick}
              >
                More Nearby Theatres
              </CinemarkLink>
            </div>
          </>
        }
      </CardContent>
    </Card>
  );
};
